/* @jsx h */
import { CheckoutResult } from "@lemmonhq/types";
import * as Sentry from "@sentry/vue";
import { defineComponent, h, ref } from "vue";
import { useShop, useAuth } from "../../providers";
import { useLocalization } from "@lemmonhq/uikit";
import { DigitalReceiptModal } from "./DigitalReceiptModal";

export type TransactionSuccessProps = {
  result: CheckoutResult;
  onSuccess: Function;
  onCancel: Function;
  onDigitalReceiptOpen?: () => void;
  /** Hide the Close button (split/partial flow controls closing itself). */
  hideClose?: boolean;
  /** When set (a per-leg collected result), shows a "Continue" action. */
  onCollectNext?: () => void;
  /** Print handler for a per-leg receipt (leg has no order-level printJobs). */
  onPrintLeg?: () => void;
  /** Running gross coverage collected so far (incl. this leg), for the per-leg
   * screen's "collected so far" subline. */
  collectedTotal?: number;
  /** The checkout's coverage target, shown alongside collectedTotal. */
  targetTotal?: number;
};

export const TransactionSuccess = defineComponent<TransactionSuccessProps>({
  name: "TransactionSuccess",
  props: [
    "result",
    "onSuccess",
    "onCancel",
    "onDigitalReceiptOpen",
    "hideClose",
    "onCollectNext",
    "onPrintLeg",
    "collectedTotal",
    "targetTotal",
  ],
  setup(props) {
    const shop = useShop();
    const auth = useAuth();
    const { t } = useLocalization();
    const showDigitalReceiptModal = ref(false);

    const SuccessCheck = () => (
      <div class="flex justify-center items-center">
        <svg
          width="214"
          height="214"
          viewBox="0 0 214 214"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="1.50134"
            y="1.50317"
            width="211"
            height="211"
            rx="105.5"
            stroke="oklch(var(--a))"
            stroke-width="3"
          />
          <path
            d="M68.0015 107.503L97.0015 135.503L154.501 78.0027"
            stroke="oklch(var(--a))"
            stroke-width="5"
          />
        </svg>
      </div>
    );

    return () => {
      const { result: txSuccess } = props;
      const currency = auth.user.value?.restaurant.currency || "CHF";

      // Per-leg confirmation for a split/partial checkout: the leg is paid but
      // the order isn't finalized yet, so no receipt/close — just confirm and
      // continue to the next leg.
      if (txSuccess.type === "staff_checkout_payment_collected") {
        // Show the full amount charged for this leg: net amount + tip. `amount`
        // alone excludes the tip, which under-displayed the first leg.
        const legTotal = txSuccess.amount + (txSuccess.tip || 0);
        return (
          <>
            <div class="text-2xl">{t("tables.checkout.paid")}</div>
            <div class="text-6xl text-accent font-bold">
              {currency} {legTotal.toFixed(2)}
            </div>
            {/* Running progress: gross coverage collected so far (incl. this
                leg) against the checkout total. */}
            {props.collectedTotal != null && props.targetTotal != null ? (
              <div class="text-base text-accent/70 mt-2">
                {t("tables.checkout.collected_so_far") || "Collected so far"}:{" "}
                {currency} {props.collectedTotal.toFixed(2)} / {currency}{" "}
                {props.targetTotal.toFixed(2)}
              </div>
            ) : null}
            <div class="flex flex-col gap-6 py-8">
              <SuccessCheck />
              <div class="text-2xl text-center text-accent">
                {t("pos.checkout-success.transaction_complete")}
              </div>
              {/* Email + print for this leg's receipt. Close is hidden — the
                  split flow continues via "Continue". */}
              <div class="flex items-center justify-center gap-3">
                <button
                  class="btn btn-md btn-circle btn-ghost border border-white/20 text-accent hover:bg-white/10"
                  disabled={!txSuccess.paymentGuid}
                  onClick={() => {
                    props.onDigitalReceiptOpen?.();
                    showDigitalReceiptModal.value = true;
                  }}
                  aria-label="Email receipt"
                  title="Email receipt"
                >
                  <span class="material-symbols-outlined text-[22px]">
                    forward_to_inbox
                  </span>
                </button>
                <button
                  class="btn btn-md btn-circle btn-ghost border border-white/20 text-accent hover:bg-white/10"
                  onClick={() => props.onPrintLeg?.()}
                  aria-label="Print receipt"
                  title="Print receipt"
                >
                  <span class="material-symbols-outlined text-[22px]">
                    print
                  </span>
                </button>
              </div>
              {props.onCollectNext && (
                <button
                  class="btn btn-md btn-primary w-full"
                  onClick={() => props.onCollectNext?.()}
                >
                  {t("general.words.continue") || "Continue"}
                  <span class="material-symbols-outlined">arrow_forward</span>
                </button>
              )}
            </div>
            {showDigitalReceiptModal.value && txSuccess.paymentGuid ? (
              <DigitalReceiptModal
                paymentId={txSuccess.paymentId}
                paymentGuid={txSuccess.paymentGuid}
                total={legTotal}
                onClose={() => {
                  showDigitalReceiptModal.value = false;
                }}
              />
            ) : null}
          </>
        );
      }

      const total = txSuccess.payment.amount + (txSuccess.payment.tips || 0);
      return (
        <>
          <div class="text-2xl">TOTAL</div>
          <div class="text-6xl text-accent font-bold">
            {auth.user.value?.restaurant.currency || "CHF"} {total.toFixed(2)}
          </div>
          <div class="flex flex-col gap-8 py-8">
            <div class="flex justify-center items-center">
              <svg
                width="214"
                height="214"
                viewBox="0 0 214 214"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="1.50134"
                  y="1.50317"
                  width="211"
                  height="211"
                  rx="105.5"
                  stroke="oklch(var(--a))"
                  stroke-width="3"
                />
                <path
                  d="M68.0015 107.503L97.0015 135.503L154.501 78.0027"
                  stroke="oklch(var(--a))"
                  stroke-width="5"
                />
              </svg>
            </div>
            <div class="text-2xl text-center text-accent">
              {t("pos.checkout-success.transaction_complete")}
            </div>
            <div
              class={
                props.hideClose
                  ? "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3"
                  : "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3"
              }
            >
              {!props.hideClose && (
                <button
                  class="w-full btn btn-md btn-secondary"
                  onClick={() => {
                    if (txSuccess) {
                      props.onSuccess();
                    } else {
                      props.onCancel();
                    }
                  }}
                >
                  {t("pos.checkout-success.close_button")}
                </button>
              )}
              <button
                class="btn btn-md btn-circle btn-ghost border border-white/20 text-accent hover:bg-white/10"
                onClick={() => {
                  props.onDigitalReceiptOpen?.();
                  showDigitalReceiptModal.value = true;
                }}
                aria-label="Email receipt"
                title="Email receipt"
              >
                <span class="material-symbols-outlined text-[22px]">
                  forward_to_inbox
                </span>
              </button>
              <button
                class="w-full btn btn-md btn-primary"
                onClick={async () => {
                  if (txSuccess.type === "cart") {
                    try {
                      const jobs = txSuccess.printJobs.filter(
                        (el) => el.doc.type === "taxReceipt",
                      );
                      await Promise.all(
                        jobs.map(async (el) => {
                          return shop.print(el);
                        }),
                      );
                    } catch (e) {
                      Sentry.captureException(e);
                      console.error(e);
                    }
                  } else {
                    try {
                      await shop.printReceipt(txSuccess.taxReceipt);
                    } catch (e) {
                      Sentry.captureException(e);
                      console.error(e);
                    }
                  }
                }}
              >
                {t("pos.checkout-success.print_receipt_button")}
              </button>
            </div>
          </div>
          {showDigitalReceiptModal.value ? (
            <DigitalReceiptModal
              paymentId={txSuccess.payment.id}
              paymentGuid={txSuccess.payment.guid}
              total={total}
              onClose={() => {
                showDigitalReceiptModal.value = false;
              }}
            />
          ) : null}
        </>
      );
    };
  },
});
